import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { RiFullscreenExitLine, RiFullscreenLine } from "@remixicon/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";
import ReviewNonOmniBizCategories from "./ReviewNonOmniBizCategories";
import ReviewNonOmniCountryCount from "./ReviewNonOmniCountryCount";
import ReviewNonOmniFillRates from "./ReviewNonOmniFillRates";
import ReviewNonOmniNames from "./ReviewNonOmniNames";
import ReviewNonOmniWebsites from "./ReviewNonOmniWebsites";

interface PlacesReviewNonOmniProps {
  chain_id: string;
  brand_id: string;
  brand_name: string;
}

const PlacesReviewOmniscrapers: React.FC<PlacesReviewNonOmniProps> = ({
  chain_id,
  brand_id,
  brand_name,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [nonOmniCountries, setNonOmniCountries] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );

  const { showError } = useError();
  const { apiRequest } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  useEffect(() => {
    const fetchNonOmniCountries = async () => {
      try {
        const response = await apiRequest<
          { country_code: string | null | undefined }[]
        >(`/brand_utils/non_omni_country_list/${chain_id}`, {
          method: "GET",
        });

        if (response.data) {
          const validCountries = response.data.filter(
            (item): item is { country_code: string } =>
              typeof item.country_code === "string" &&
              item.country_code.trim() !== ""
          );

          const sortedCountries = [...validCountries].sort((a, b) =>
            a.country_code.localeCompare(b.country_code)
          );
          setNonOmniCountries(sortedCountries);

          const params = new URLSearchParams(location.search);
          const countryCode = params.get("country");
          if (
            countryCode &&
            sortedCountries.some((c) => c.country_code === countryCode)
          ) {
            setSelectedCountryCode(countryCode);
          }
        }
      } catch (error) {
        showError(
          `Failed to fetch countries for brand non-omni review dropdown. Chain ID: ${chain_id}`
        );
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchNonOmniCountries();
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const countryCode = params.get("country");
    const hash = location.hash.substring(1); // Get the hash without "#"

    // Set selected country from URL if valid
    if (countryCode && nonOmniCountries.length > 0) {
      const matchingCountry = nonOmniCountries.find(
        (c) => c.country_code.toLowerCase() === countryCode.toLowerCase()
      );

      if (matchingCountry) {
        setSelectedCountryCode(matchingCountry.country_code);
      }
    }

    // Scroll to hash after rendering
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const yOffset = -120; // Adjust for sticky headers
          const yPosition =
            element.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: yPosition, behavior: "smooth" });
        }
      }, 700);
    }
  }, [location.search, location.hash, nonOmniCountries]);

  // User Instructions for Retractions
  const user_instructions_retractions = {
    section_id: "places-retract-nonomni",
    addAnchorLink: false,
    instruction_title: "Review Non-Omni Places To Retract",
    goal: "Ensure values for each of the attributes below, associated with the selected country of operation, are valid, using the metadata as a reference.",
    task_description:
      "The table provided for each attribute compares the metadata with the actual values linked to places in the dataplor database for the brand.",
    task_steps: [
      {
        title:
          "Identify values from the places data that do not correspond to valid values in the metadata",
        instructions: [
          "Review the provided table for each attribute to identify any values associated with places in the database that are missing from the metadata. When metadata is missing, the place needs to be reviewed to assess whether it is valid.",
        ],
      },
      {
        title: "Review step",
        instructions: ["TBD"],
      },
      {
        title: "Retract Chain ID",
        instructions: [
          {
            text: "For values that indicate places that should not be associated with the brand, retract `chain_id` in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Review All`.",
              "Open the SQL editor.",
              "Copy and paste the provided SQL code for each attribute to view the places that require `chain_id` to be retracted. Be sure to update with the correct `external_id` and country code.",
            ],
          },
        ],
      },
    ],
  };

  // User Instructions for Location Name & Fill Rates
  const user_instructions_fillrates = {
    section_id: "places-nonomni-fillrates",
    addAnchorLink: false,
    instruction_title: "Review Non-Omnis Fill Rates",
    task_steps: [
      {
        title: "Review Fill Rates",
        instructions: [
          {
            text: "If the fill rates for any of the following attributes are < 1, copy the link below and escalate to Karen or Monica.",
            sublist: ["Name", "Address", "Business category", "Coordinates"],
          },
        ],
      },
    ],
  };

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              overflow: "auto",
              p: 5,
              pt: 0,
            }
          : {
              position: "relative",
              width: "auto",
              height: "auto",
              minHeight: "500px",
              overflow: "hidden",
              border: "1px solid #ccc",
              borderRadius: "4px",
              p: 5,
              mt: 2,
              backgroundColor: "#fff",
            }
      }
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          ...(isFullscreen && {
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            width: "100%",
            zIndex: 1000,
            p: 2,
          }),
        }}
      >
        <Typography variant="h5">
          {isFullscreen
            ? `Brand Non-Omni Review: ${brand_name} - ${
                selectedCountryCode?.toUpperCase() ||
                "Select an country to review"
              }`
            : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Open Kuebiko Button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              <img
                src="/images/dataplor_logo.svg"
                alt="dataplor logo"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={() =>
              window.open(
                `https://kuebiko.dataplor.com/brands/${brand_id}/review`,
                "_blank"
              )
            }
          >
            Open Kuebiko
          </Button>
          {/* Fullscreen Toggle Button */}
          <IconButton onClick={toggleFullscreen} sx={{ zIndex: 500 }}>
            {isFullscreen ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: 2, pl: 2, pr: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Non-Omni Places Data Review
          </Typography>
        </Box>

        <Box sx={{ mt: 5 }}>
          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" gutterBottom>
              Review Non-Omni Places Data by Country
            </Typography>
            <Typography paragraph sx={{ fontStyle: "italic" }}>
              Iterate through one country at a time. Select a country below to
              update the data for review.
            </Typography>
          </Box>
          {/* Country Code Dropdown */}
          <Box sx={{ mt: 2, mb: 5, width: 300 }}>
            {loadingCountries ? (
              <CircularProgress />
            ) : (
              <Autocomplete
                options={nonOmniCountries}
                getOptionLabel={(option) =>
                  `${option.country_code.toUpperCase()}: ${option.country_name}`
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.country_code
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                      option.country_name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Country"
                    variant="outlined"
                  />
                )}
                value={
                  nonOmniCountries.length > 0
                    ? nonOmniCountries.find(
                        (country) =>
                          country.country_code === selectedCountryCode
                      ) || null
                    : null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedCountryCode(newValue.country_code);
                    navigate(
                      { search: `?country=${newValue.country_code}` },
                      { replace: true }
                    );
                  } else {
                    setSelectedCountryCode(null);
                    navigate({ search: "" }, { replace: true });
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option.country_code === value.country_code
                }
              />
            )}
          </Box>
          {/* Country Specific Content */}
          {selectedCountryCode && (
            <Box>
              <BrandReviewInstructions {...user_instructions_retractions} />
              <ReviewNonOmniBizCategories
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
              <ReviewNonOmniNames
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
              <ReviewNonOmniWebsites
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
              <ReviewNonOmniCountryCount
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
              <BrandReviewInstructions {...user_instructions_fillrates} />
              <ReviewNonOmniFillRates
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlacesReviewOmniscrapers;
